import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Pagination } from './Pagination/Pagination';
import './PdfViewer.scss';
import { PdfViewerSkeleton } from './PdfViewerSkeleton';
import { usePdfViewer } from './usePdfViewer';
import React from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { CustomBottomBar } from '@components/BottomBar/CustomBottomBar';
import { Button } from '@design-system/components/Button/Button';
import { Icon } from '@design-system/components/Icon/Icon';
import { LiaArrowLeftSolid } from 'react-icons/lia';
import { Typography } from '@design-system/components/Typography/Typography';

export type PdfViewerProps = {
    file: string | File;
    setHasSeenAllPages: React.Dispatch<React.SetStateAction<boolean>> | ((hasSeenAllPages: boolean) => void);
    bottomBarPagination?: boolean;
    callbackOnLastPage?: () => void;
    labelNextButtonOnLastPage?: string;
};

export const PdfViewer: React.FC<PdfViewerProps> = ({
    file,
    setHasSeenAllPages,
    bottomBarPagination = false,
    callbackOnLastPage,
    labelNextButtonOnLastPage
}) => {
    const { currentPage, numPages, setCurrentPage, onDocumentLoadSuccess, isLoading, handlers, onItemClick, canPan, setCanPan } =
        usePdfViewer({
            setHasSeenAllPages
        });

    const transformRef = React.useRef<ReactZoomPanPinchRef | null>(null);

    return (
        <>
            {isLoading && <PdfViewerSkeleton />}
            <div {...handlers}>
                <Document
                    file={file}
                    className="pdf-viewer"
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={''}
                    externalLinkRel="_blank"
                    onItemClick={onItemClick}
                >
                    <div style={{ height: '450px' }} onTouchMove={canPan ? (e) => e.preventDefault() : undefined}>
                        <TransformWrapper
                            ref={transformRef}
                            initialScale={1}
                            minScale={1}
                            maxScale={4}
                            doubleClick={{ disabled: true }}
                            pinch={{ disabled: false }}
                            panning={{ disabled: !canPan }}
                            wheel={{ disabled: true }}
                            onZoomStop={() => {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                const instance = transformRef.current?.instance as any;
                                if (instance) {
                                    const currentScale = instance.transformState.scale;
                                    setCanPan(currentScale > 1);
                                }
                            }}
                        >
                            <TransformComponent wrapperStyle={{ touchAction: 'auto' }}>
                                <Page scale={1} pageNumber={currentPage} width={window.innerWidth} loading={''} />
                            </TransformComponent>
                        </TransformWrapper>
                    </div>
                </Document>
            </div>
            {numPages && numPages > 1 && !bottomBarPagination ? (
                <Pagination currentPage={currentPage} numPages={numPages} setCurrentPage={setCurrentPage} />
            ) : null}
            {numPages && numPages > 1 && bottomBarPagination ? (
                <CustomBottomBar>
                    <Button
                        color="marine"
                        variant="secondary"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="sm-bottom-bar-button mr-small-spacing"
                    >
                        <Icon className="arrow-left" color="grey" icon={<LiaArrowLeftSolid />} size={30} />
                    </Button>
                    <Button
                        variant="primary"
                        color="marine"
                        size="fullwidth"
                        onClick={() => {
                            if (currentPage === numPages && callbackOnLastPage) {
                                callbackOnLastPage();
                            } else {
                                setCurrentPage(currentPage + 1);
                            }
                        }}
                        disabled={currentPage === numPages && !callbackOnLastPage}
                        className="next-button"
                    >
                        <Typography intrinsic="h4">
                            {currentPage === numPages && labelNextButtonOnLastPage ? labelNextButtonOnLastPage : 'Page suivante'}
                        </Typography>
                    </Button>
                </CustomBottomBar>
            ) : null}
        </>
    );
};
